import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { AdminDashboardKpi } from './types'

function formatRub(rub: number): string {
  if (Math.abs(rub) >= 1_000_000) return `₽ ${(rub / 1_000_000).toFixed(1)}M`
  if (Math.abs(rub) >= 1_000) return `₽ ${(rub / 1_000).toFixed(0)}K`
  return `₽ ${rub.toFixed(0)}`
}

function Delta({ value }: { value: number | null }) {
  if (value === null) return null
  const up = value > 0
  const zero = value === 0
  const cls = zero ? 'text-gray-400' : up ? 'text-emerald-600' : 'text-red-600'
  const arrow = zero ? '' : up ? '↑' : '↓'
  return (
    <span className={`text-xs font-medium ${cls}`}>
      {arrow} {Math.abs(value)}% к прошлому
    </span>
  )
}

interface CardProps {
  label: string
  value: ReactNode
  tone?: 'neutral' | 'red' | 'amber' | 'emerald'
  hint?: ReactNode
  to?: string
  onClick?: () => void
}

function Card({ label, value, tone = 'neutral', hint, to, onClick }: CardProps) {
  const bg = {
    neutral: 'bg-gray-50 border-gray-200',
    red: 'bg-red-50/60 border-red-100',
    amber: 'bg-amber-50/60 border-amber-100',
    emerald: 'bg-emerald-50/60 border-emerald-100',
  }[tone]

  const inner = (
    <div className={`rounded-2xl border ${bg} p-4 h-full flex flex-col justify-between`}>
      <div className="text-sm text-gray-600">{label}</div>
      <div className="text-3xl font-bold text-gray-900 leading-tight mt-2">{value}</div>
      {hint && <div className="mt-1">{hint}</div>}
    </div>
  )
  return to ? (
    <Link to={to} className="block hover:shadow-sm transition-shadow">
      {inner}
    </Link>
  ) : onClick ? (
    <button
      type="button"
      onClick={onClick}
      className="block w-full text-left hover:shadow-sm transition-shadow rounded-2xl"
    >
      {inner}
    </button>
  ) : (
    inner
  )
}

export function KpiCards({
  kpi,
  deadlineTo,
  onUserActivityClick,
}: {
  kpi: AdminDashboardKpi
  /** Конец выбранного периода (YYYY-MM-DD) — чтобы клик по «Горящим дедлайнам»
   *  открывал ровно те заявки, которые посчитаны на карточке. */
  deadlineTo?: string
  onUserActivityClick: () => void
}) {
  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
      <Card
        label="Активных заявок"
        value={kpi.activeRequests.current}
        hint={<Delta value={kpi.activeRequests.deltaPercent} />}
        to="/requests?filter=active"
      />
      <Card
        label="Сумма в работе"
        value={formatRub(kpi.sumInWork.currentRub)}
        hint={<Delta value={kpi.sumInWork.deltaPercent} />}
      />
      <Card
        label="Сейчас онлайн"
        value={
          <>
            {kpi.activeUsers.active}
            <span className="text-xl text-gray-400"> / {kpi.activeUsers.total}</span>
          </>
        }
        hint={
          <span className="text-xs text-gray-500">
            {kpi.activeUsers.total - kpi.activeUsers.active} вне сети
          </span>
        }
        onClick={onUserActivityClick}
      />
      <Card
        label="Горящих дедлайнов"
        value={kpi.burningDeadlines.count}
        tone="red"
        hint={<span className="text-xs text-gray-500">дедлайн в выбранном периоде</span>}
        to={`/requests?filter=burning${deadlineTo ? `&deadlineTo=${deadlineTo}` : ''}`}
      />
      <Card
        label="Без движения > 7д"
        value={kpi.staleRequests.count}
        tone="amber"
        hint={<span className="text-xs text-amber-700">требуют внимания</span>}
        to="/requests?filter=stale"
      />
      <Card
        label="КП отправлено"
        value={kpi.kpSent.count}
        tone="emerald"
        hint={<span className="text-xs text-emerald-700">ср. {kpi.kpSent.dailyAverage}/день</span>}
        to="/requests?status=QUOTE_SENT"
      />
    </div>
  )
}
